// Field Validations Configuration
// Add field validations here with fieldName as key
// Each field can have: regex, message, required, minLength, maxLength, min, max, noPastDates

// Date field names for cross-validation
export const DATE_FIELDS = {
  DUE_DATE: "DueDate",
  DATA_DELIVERY_DATE: "DataDeliveryDate",
  AUDIT_FILING_DATE: "AuditReportDate",
  DATA_PERIOD_START_DATE: "DataPeriodStartDate",
  DATA_PERIOD_END_DATE: "DataPeriodEndDate",
  ANALYSIS_PERIOD_DATE: "AnalysisPeriodDate",
  ASSESSMENT_START_DATE: "DataAssessmentStartDate1",
  ASSESSMENT_END_DATE: "DataAssessmentEndDate1",
  PREPARING_ANALYZER_START_DATE: "PreparingAnalyzerStartDate",
  PREPARING_ANALYZER_END_DATE: "PreparingAnalyzerEndDate",
  TOOL_ISSUE_START_DATE: "ToolIssueStartDate",
  TOOL_ISSUE_END_DATE: "ToolIssueEndDate",
};

const createLocalDate = (year, month, day) => {
  const normalizedYear = Number(year);
  const normalizedMonth = Number(month);
  const normalizedDay = Number(day);

  if (
    !Number.isInteger(normalizedYear) ||
    !Number.isInteger(normalizedMonth) ||
    !Number.isInteger(normalizedDay)
  ) {
    return null;
  }

  if (normalizedYear < 1000 || normalizedYear > 9999) {
    return null;
  }

  const date = new Date(normalizedYear, normalizedMonth - 1, normalizedDay);

  if (isNaN(date.getTime())) {
    return null;
  }

  if (
    date.getFullYear() !== normalizedYear ||
    date.getMonth() !== normalizedMonth - 1 ||
    date.getDate() !== normalizedDay
  ) {
    return null;
  }

  return date;
};

// Helper to parse date string to a local Date object for comparison
const parseDate = (dateString) => {
  if (!dateString) return null;

  const normalizedInput = String(dateString).trim();

  if (!normalizedInput) {
    return null;
  }

  if (
    /[T:+]/.test(normalizedInput) ||
    /^\d{1,2}\/\d{1,2}\/\d{4}\s+\d/.test(normalizedInput)
  ) {
    const timestampDate = new Date(normalizedInput);

    if (!isNaN(timestampDate.getTime())) {
      return createLocalDate(
        timestampDate.getFullYear(),
        timestampDate.getMonth() + 1,
        timestampDate.getDate(),
      );
    }
  }

  let dateOnly = normalizedInput;
  if (dateOnly.includes(" ")) {
    dateOnly = dateOnly.split(" ")[0];
  }

  if (dateOnly.includes("/")) {
    const parts = dateOnly.split("/");

    if (parts.length === 3 && parts[2].length === 4) {
      const [firstPart, secondPart, year] = parts;
      const firstNumber = Number(firstPart);
      const secondNumber = Number(secondPart);
      const isDayMonthFormat = firstNumber > 12 || secondNumber <= 12;
      const day = isDayMonthFormat ? firstPart : secondPart;
      const month = isDayMonthFormat ? secondPart : firstPart;

      return createLocalDate(year, month, day);
    }
  }

  if (dateOnly.includes("-") && !dateOnly.includes("T")) {
    const parts = dateOnly.split("-");

    if (parts.length === 3) {
      if (parts[0].length === 4) {
        const [year, month, day] = parts;
        return createLocalDate(year, month, day);
      }

      if (parts[2].length === 4) {
        const [day, month, year] = parts;
        return createLocalDate(year, month, day);
      }
    }
  }

  const fallbackDate = new Date(normalizedInput);

  if (isNaN(fallbackDate.getTime())) {
    return null;
  }

  return createLocalDate(
    fallbackDate.getFullYear(),
    fallbackDate.getMonth() + 1,
    fallbackDate.getDate(),
  );
};

const createDateParts = (year, month, day) => {
  const normalizedYear = Number(year);
  const normalizedMonth = Number(month);
  const normalizedDay = Number(day);

  if (
    !Number.isInteger(normalizedYear) ||
    !Number.isInteger(normalizedMonth) ||
    !Number.isInteger(normalizedDay)
  ) {
    return null;
  }

  if (normalizedYear < 1000 || normalizedYear > 9999) {
    return null;
  }

  if (normalizedMonth < 1 || normalizedMonth > 12) {
    return null;
  }

  if (normalizedDay < 1 || normalizedDay > 31) {
    return null;
  }

  return {
    year: normalizedYear,
    month: normalizedMonth,
    day: normalizedDay,
  };
};

const parseComparableDate = (dateString) => {
  if (!dateString) {
    return null;
  }

  const strictDate = parseDate(dateString);

  if (strictDate) {
    return createDateParts(
      strictDate.getFullYear(),
      strictDate.getMonth() + 1,
      strictDate.getDate(),
    );
  }

  const normalizedInput = String(dateString).trim();

  if (!normalizedInput) {
    return null;
  }

  let dateOnly = normalizedInput;
  if (dateOnly.includes(" ")) {
    dateOnly = dateOnly.split(" ")[0];
  }

  if (dateOnly.includes("/")) {
    const parts = dateOnly.split("/");

    if (parts.length === 3 && parts[2].length === 4) {
      const [firstPart, secondPart, year] = parts;
      const firstNumber = Number(firstPart);
      const secondNumber = Number(secondPart);
      const isDayMonthFormat = firstNumber > 12 || secondNumber <= 12;
      const day = isDayMonthFormat ? firstPart : secondPart;
      const month = isDayMonthFormat ? secondPart : firstPart;

      return createDateParts(year, month, day);
    }
  }

  if (dateOnly.includes("-") && !dateOnly.includes("T")) {
    const parts = dateOnly.split("-");

    if (parts.length === 3) {
      if (parts[0].length === 4) {
        const [year, month, day] = parts;
        return createDateParts(year, month, day);
      }

      if (parts[2].length === 4) {
        const [day, month, year] = parts;
        return createDateParts(year, month, day);
      }
    }
  }

  return null;
};

const compareDateParts = (leftDate, rightDate) => {
  if (leftDate.year !== rightDate.year) {
    return leftDate.year - rightDate.year;
  }

  if (leftDate.month !== rightDate.month) {
    return leftDate.month - rightDate.month;
  }

  return leftDate.day - rightDate.day;
};

export const isValidDateFormatValue = (dateString) => {
  if (!dateString) {
    return false;
  }

  return Boolean(parseComparableDate(dateString));
};

export const isValidDateValue = (dateString) => {
  if (!dateString) {
    return false;
  }

  return Boolean(parseDate(dateString));
};

// Get field value from fieldValues array or original order data
const getFieldValue = (fieldKey, allFieldValues, orderDetails) => {
  // First check if there's an edited value
  const editedField = allFieldValues?.find((f) => f.fieldName === fieldKey);
  if (editedField) {
    return editedField.updatedValue;
  }

  // Fall back to original order data
  if (orderDetails) {
    for (const section of orderDetails) {
      const field = section.pagefields?.find((f) => f.field === fieldKey);
      if (field?.fieldValue) {
        return field.fieldValue;
      }
    }
  }

  return null;
};

// Validate date cross-field relationships
export const validateDateRelationships = (
  fieldKey,
  value,
  allFieldValues,
  orderDetails,
) => {
  if (!value) return { isValid: true, message: "" };

  const currentDate = parseComparableDate(value);
  if (!currentDate) return { isValid: true, message: "" };

  // Helper to get parsed date for a field
  const getDateValue = (key) => {
    const val = getFieldValue(key, allFieldValues, orderDetails);
    return parseComparableDate(val);
  };

  const dueDate =
    fieldKey === DATE_FIELDS.DUE_DATE
      ? currentDate
      : getDateValue(DATE_FIELDS.DUE_DATE);
  const dataDeliveryDate =
    fieldKey === DATE_FIELDS.DATA_DELIVERY_DATE
      ? currentDate
      : getDateValue(DATE_FIELDS.DATA_DELIVERY_DATE);
  const auditFilingDate =
    fieldKey === DATE_FIELDS.AUDIT_FILING_DATE
      ? currentDate
      : getDateValue(DATE_FIELDS.AUDIT_FILING_DATE);
  const periodStartDate =
    fieldKey === DATE_FIELDS.DATA_PERIOD_START_DATE
      ? currentDate
      : getDateValue(DATE_FIELDS.DATA_PERIOD_START_DATE);
  const periodEndDate =
    fieldKey === DATE_FIELDS.DATA_PERIOD_END_DATE
      ? currentDate
      : getDateValue(DATE_FIELDS.DATA_PERIOD_END_DATE);
  const assessmentStartDate =
    fieldKey === DATE_FIELDS.ASSESSMENT_START_DATE
      ? currentDate
      : getDateValue(DATE_FIELDS.ASSESSMENT_START_DATE);
  const assessmentEndDate =
    fieldKey === DATE_FIELDS.ASSESSMENT_END_DATE
      ? currentDate
      : getDateValue(DATE_FIELDS.ASSESSMENT_END_DATE);
  const preparingAnalyzerStartDate =
    fieldKey === DATE_FIELDS.PREPARING_ANALYZER_START_DATE
      ? currentDate
      : getDateValue(DATE_FIELDS.PREPARING_ANALYZER_START_DATE);
  const preparingAnalyzerEndDate =
    fieldKey === DATE_FIELDS.PREPARING_ANALYZER_END_DATE
      ? currentDate
      : getDateValue(DATE_FIELDS.PREPARING_ANALYZER_END_DATE);
  const toolIssueStartDate =
    fieldKey === DATE_FIELDS.TOOL_ISSUE_START_DATE
      ? currentDate
      : getDateValue(DATE_FIELDS.TOOL_ISSUE_START_DATE);
  const toolIssueEndDate =
    fieldKey === DATE_FIELDS.TOOL_ISSUE_END_DATE
      ? currentDate
      : getDateValue(DATE_FIELDS.TOOL_ISSUE_END_DATE);

  // Helper to compare dates (ignoring time)

  const isBefore = (date1, date2) => compareDateParts(date1, date2) < 0;
  const isBeforeOrEqual = (date1, date2) => compareDateParts(date1, date2) <= 0;
  const isAfter = (date1, date2) => compareDateParts(date1, date2) > 0;
  const isAfterOrEqual = (date1, date2) => compareDateParts(date1, date2) >= 0;

  switch (fieldKey) {
    case DATE_FIELDS.DUE_DATE:
      // Due Date should be on or after Data Delivery Date
      if (dataDeliveryDate && isBefore(currentDate, dataDeliveryDate)) {
        return {
          isValid: false,
          message: "Due Date should be on or after Data Delivery Date",
        };
      }
      // Due Date should be before Audit Filing Date
      if (auditFilingDate && isAfterOrEqual(currentDate, auditFilingDate)) {
        return {
          isValid: false,
          message: "Due Date should be before Audit Filing Date",
        };
      }
      // Due Date should be on or after Data Period Start Date
      if (periodStartDate && isBefore(currentDate, periodStartDate)) {
        return {
          isValid: false,
          message: "Due Date should be on or after Data Period Start Date",
        };
      }
      // Due Date should be on or after Data Period End Date
      if (periodEndDate && isBefore(currentDate, periodEndDate)) {
        return {
          isValid: false,
          message: "Due Date should be on or after Data Period End Date",
        };
      }
      break;

    case DATE_FIELDS.DATA_DELIVERY_DATE:
      // Data Delivery Date should be on or before Due Date
      if (dueDate && isAfter(currentDate, dueDate)) {
        return {
          isValid: false,
          message: "Data Delivery Date should be on or before Due Date",
        };
      }
      // Data Delivery Date should be on or after Data Period Start Date
      if (periodStartDate && isBefore(currentDate, periodStartDate)) {
        return {
          isValid: false,
          message:
            "Data Delivery Date should be on or after Data Period Start Date",
        };
      }
      // Data Delivery Date should be on or after Data Period End Date
      if (periodEndDate && isBefore(currentDate, periodEndDate)) {
        return {
          isValid: false,
          message:
            "Data Delivery Date should be on or after Data Period End Date",
        };
      }
      break;

    case DATE_FIELDS.DATA_PERIOD_END_DATE:
      // Data Period End Date should be on or after Data Period Start Date
      if (periodStartDate && isBefore(currentDate, periodStartDate)) {
        return {
          isValid: false,
          message:
            "Data Period End Date should be on or after Data Period Start Date",
        };
      }
      // Data Period End Date should be on or before Due Date
      if (dueDate && isAfter(currentDate, dueDate)) {
        return {
          isValid: false,
          message: "Data Period End Date should be on or before Due Date",
        };
      }
      // Data Period End Date should be on or before Data Delivery Date
      if (dataDeliveryDate && isAfter(currentDate, dataDeliveryDate)) {
        return {
          isValid: false,
          message:
            "Data Period End Date should be on or before Data Delivery Date",
        };
      }
      // Data Period End Date should be on or before Audit Filing Date
      if (auditFilingDate && isAfter(currentDate, auditFilingDate)) {
        return {
          isValid: false,
          message:
            "Data Period End Date should be on or before Audit Filing Date",
        };
      }
      break;

    case DATE_FIELDS.DATA_PERIOD_START_DATE:
      // Data Period Start Date should be on or before Data Period End Date
      if (periodEndDate && isAfter(currentDate, periodEndDate)) {
        return {
          isValid: false,
          message:
            "Data Period Start Date should be on or before Data Period End Date",
        };
      }
      // Data Period Start Date should be on or before Due Date
      if (dueDate && isAfter(currentDate, dueDate)) {
        return {
          isValid: false,
          message: "Data Period Start Date should be on or before Due Date",
        };
      }
      // Data Period Start Date should be on or before Data Delivery Date
      if (dataDeliveryDate && isAfter(currentDate, dataDeliveryDate)) {
        return {
          isValid: false,
          message:
            "Data Period Start Date should be on or before Data Delivery Date",
        };
      }
      // Data Period Start Date should be before Audit Filing Date
      if (auditFilingDate && isAfterOrEqual(currentDate, auditFilingDate)) {
        return {
          isValid: false,
          message: "Data Period Start Date should be before Audit Filing Date",
        };
      }
      break;

    case DATE_FIELDS.AUDIT_FILING_DATE:
      // Audit Filing Date should be after Due Date
      if (dueDate && isBeforeOrEqual(currentDate, dueDate)) {
        return {
          isValid: false,
          message: "Audit Filing Date should be after Due Date",
        };
      }
      // Audit Filing Date should be on or after Data Period End Date
      if (periodEndDate && isBefore(currentDate, periodEndDate)) {
        return {
          isValid: false,
          message:
            "Audit Filing Date should be on or after Data Period End Date",
        };
      }
      break;

    case DATE_FIELDS.ASSESSMENT_START_DATE:
      // Assessment Start Date should be on or before Assessment End Date
      if (assessmentEndDate && isAfter(currentDate, assessmentEndDate)) {
        return {
          isValid: false,
          message:
            "Assessment Start Date should be on or before Assessment End Date",
        };
      }
      break;
    case DATE_FIELDS.ASSESSMENT_END_DATE:
      // Assessment End Date should be on or after Assessment Start Date
      if (assessmentStartDate && isBefore(currentDate, assessmentStartDate)) {
        return {
          isValid: false,
          message:
            "Assessment End Date should be on or after Assessment Start Date",
        };
      }
      break;
    case DATE_FIELDS.PREPARING_ANALYZER_START_DATE:
      // Preparing Analyzer Start Date should be on or before Preparing Analyzer End Date
      if (
        preparingAnalyzerEndDate &&
        isAfter(currentDate, preparingAnalyzerEndDate)
      ) {
        return {
          isValid: false,
          message:
            "Preparing Analyzer Start Date should be on or before Preparing Analyzer End Date",
        };
      }
      break;
    case DATE_FIELDS.PREPARING_ANALYZER_END_DATE:
      // Preparing Analyzer End Date should be on or after Preparing Analyzer Start Date
      if (
        preparingAnalyzerStartDate &&
        isBefore(currentDate, preparingAnalyzerStartDate)
      ) {
        return {
          isValid: false,
          message:
            "Preparing Analyzer End Date should be on or after Preparing Analyzer Start Date",
        };
      }
      break;
    case DATE_FIELDS.TOOL_ISSUE_START_DATE:
      // Tool Issue Start Date should be on or before Tool Issue End Date
      if (toolIssueEndDate && isAfter(currentDate, toolIssueEndDate)) {
        return {
          isValid: false,
          message:
            "Tool Issue Start Date should be on or before Tool Issue End Date",
        };
      }
      break;
    case DATE_FIELDS.TOOL_ISSUE_END_DATE:
      // Tool Issue End Date should be on or after Tool Issue Start Date
      if (toolIssueStartDate && isBefore(currentDate, toolIssueStartDate)) {
        return {
          isValid: false,
          message:
            "Tool Issue End Date should be on or after Tool Issue Start Date",
        };
      }
      break;

    default:
      break;
  }

  return { isValid: true, message: "" };
};

// Check if a field is a date relationship field
export const isDateRelationshipField = (fieldName) => {
  return Object.values(DATE_FIELDS).includes(fieldName);
};

const EY_OR_SRB_EMAIL_REGEX = /^[^\s@]+@(?:(?:[^\s@]+\.)*ey\.com|srb\.in)$/i;

const Efforts_and_Savings = {
  Reason: {
    regex: /^[A-Za-z0-9 \/\\|:;&#()_$@',-]{0,100}$/,
    message:
      "Only alphanumeric characters and / \\ | : ; & # ( ) _ $ @ ' , - are allowed, up to 100 characters.",
  },
  EffortHours: {
    regex: /^\d*\.?\d*$/,
    message: "Only numbers are allowed",
    requiredMessage: "This field is required",
    required: true,
  },
  EffortForMasterId: {
    requiredMessage: "This field is required",
    required: true,
  },
  ResourceName: {
    regex: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
    message: "Only valid emails are allowed.",
    requiredMessage: "This field is required",
    required: true,
  },
  UserName: {
    regex: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
    message: "Only valid emails are allowed.",
    requiredMessage: "This field is required",
    required: true,
  },
  Username: {
    regex: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
    message: "Only valid emails are allowed.",
    requiredMessage: "This field is required",
    required: true,
  },
  ApplicationUsedId: {
    requiredMessage: "This field is required",
    required: true,
  },
  HoursToSave: {
    regex: /^\d*\.?\d*$/,
    required: true,
    message: "Only numbers are allowed",
    requiredMessage: "This field is required",
  },
  Role: {
    requiredMessage: "This field is required",
    required: true,
  },
  OtherReason: {
    regex: /^[A-Za-z0-9 \/\\|:;&#()_$@',-]{0,100}$/,
    message:
      "Only alphanumeric characters and / \\ | : ; & # ( ) _ $ @ ' , - are allowed, up to 100 characters.",
  },
};

const NumericFields = {
  MercuryClientId: {
    regex: /^\d{8}$/,
    message: "Only 8 digit numbers are allowed",
  },
  MercuryId: {
    regex: /^\d{8}$/,
    message: "Only 8 digit numbers are allowed",
  },
  CYLineCount: {
    regex: /^\d*$/,
    message: "Only whole numbers are allowed",
  },
  PYLineCount: {
    regex: /^\d*$/,
    message: "Only whole numbers are allowed",
  },
  NumberOfEntityBusinessUnit: {
    regex: /^\d*$/,
    message: "Only whole numbers are allowed",
  },
  PerformerPlannedEffort: {
    regex: /^\d*\.?\d*$/,
    message: "Only numbers are allowed",
  },
  ReviewerPlannedEffort: {
    regex: /^\d*\.?\d*$/,
    message: "Only numbers are allowed",
  },
  BudgetedEffort: {
    regex: /^\d*\.?\d*$/,
    message: "Only numbers are allowed",
  },
};

const TextFieldsWithSomeSpecialCharacters = {
  "ERP Version": {
    regex: /^[A-Za-z0-9 \/\\|:;&#()_$@',-]{0,100}$/,
    message:
      "Only alphanumeric characters and / \\ | : ; & # ( ) _ $ @ ' , - are allowed, up to 100 characters.",
  },
  ERPVersion: {
    regex: /^[A-Za-z0-9 \/\\|:;&#()_$@',-]{0,100}$/,
    message:
      "Only alphanumeric characters and / \\ | : ; & # ( ) _ $ @ ' , - are allowed, up to 100 characters.",
  },
  OtherERPName: {
    regex: /^[A-Za-z0-9 \/\\|:;&#()_$@',-]{0,100}$/,
    message:
      "Only alphanumeric characters and / \\ | : ; & # ( ) _ $ @ ' , - are allowed, up to 100 characters.",
  },
  IncompleteDataNeeded: {
    regex: /^[A-Za-z0-9 \/\\|:;&#()_$@',-]{0,100}$/,
    message:
      "Only alphanumeric characters and / \\ | : ; & # ( ) _ $ @ ' , - are allowed, up to 100 characters.",
  },
  OtherExtractionTool: {
    regex: /^[A-Za-z0-9 \/\\|:;&#()_$@',-]{0,100}$/,
    message:
      "Only alphanumeric characters and / \\ | : ; & # ( ) _ $ @ ' , - are allowed, up to 100 characters.",
  },
  ToolIssueDescription: {
    regex: /^[A-Za-z0-9 \/\\|:;&#()_$@',-]{0,100}$/,
    message:
      "Only alphanumeric characters and / \\ | : ; & # ( ) _ $ @ ' , - are allowed, up to 100 characters.",
  },
  ToolIssueITTicket: {
    regex: /^[A-Za-z0-9 \/\\|:;&#()_$@',-]{0,100}$/,
    message:
      "Only alphanumeric characters and / \\ | : ; & # ( ) _ $ @ ' , - are allowed, up to 100 characters.",
  },
  ReprocessDescription: {
    regex: /^[A-Za-z0-9 \/\\|:;&#()_$@',-]{0,100}$/,
    message:
      "Only alphanumeric characters and / \\ | : ; & # ( ) _ $ @ ' , - are allowed, up to 100 characters.",
  },
  AlternateCodeNumber: {
    regex: /^[A-Za-z0-9 \/\\|:;&#()_$@',-]{0,100}$/,
    message:
      "Only alphanumeric characters and / \\ | : ; & # ( ) _ $ @ ' , - are allowed, up to 100 characters.",
  },
  TideOrNonTide: {
    regex: /^[A-Za-z0-9 \/\\|:;&#()_$@',-]{0,100}$/,
    message:
      "Only alphanumeric characters and / \\ | : ; & # ( ) _ $ @ ' , - are allowed, up to 100 characters.",
  },
  UserDefined1: {
    regex: /^[A-Za-z0-9 \/\\|:;&#()_$@',-]{0,100}$/,
    message:
      "Only alphanumeric characters and / \\ | : ; & # ( ) _ $ @ ' , - are allowed, up to 100 characters.",
  },
  UserDefined2: {
    regex: /^[A-Za-z0-9 \/\\|:;&#()_$@',-]{0,100}$/,
    message:
      "Only alphanumeric characters and / \\ | : ; & # ( ) _ $ @ ' , - are allowed, up to 100 characters.",
  },
  UserDefined3: {
    regex: /^[A-Za-z0-9 \/\\|:;&#()_$@',-]{0,100}$/,
    message:
      "Only alphanumeric characters and / \\ | : ; & # ( ) _ $ @ ' , - are allowed, up to 100 characters.",
  },
  UserDefined4: {
    regex: /^[A-Za-z0-9 \/\\|:;&#()_$@',-]{0,100}$/,
    message:
      "Only alphanumeric characters and / \\ | : ; & # ( ) _ $ @ ' , - are allowed, up to 100 characters.",
  },
  UserDefined5: {
    regex: /^[A-Za-z0-9 \/\\|:;&#()_$@',-]{0,100}$/,
    message:
      "Only alphanumeric characters and / \\ | : ; & # ( ) _ $ @ ' , - are allowed, up to 100 characters.",
  },
  SharedDriveURL: {
    regex: /^[A-Za-z0-9 \/\\|:;&#()_$@',-]{0,100}$/,
    message:
      "Only alphanumeric characters and / \\ | : ; & # ( ) _ $ @ ' , - are allowed, up to 100 characters.",
  },
  ClonedFrom: {
    regex: /^[A-Za-z0-9 \/\\|:;&#()_$@',-]{0,100}$/,
    message:
      "Only alphanumeric characters and / \\ | : ; & # ( ) _ $ @ ' , - are allowed, up to 100 characters.",
  },
  OtherCancellationReason: {
    regex: /^[A-Za-z0-9 \/\\|:;&#()_$@',-]{0,100}$/,
    message:
      "Only alphanumeric characters and / \\ | : ; & # ( ) _ $ @ ' , - are allowed, up to 100 characters.",
  },
  OtherOnHoldReason: {
    regex: /^[A-Za-z0-9 \/\\|:;&#()_$@',-]{0,100}$/,
    message:
      "Only alphanumeric characters and / \\ | : ; & # ( ) _ $ @ ' , - are allowed, up to 100 characters.",
  },
  OtherCleansingTool: {
    regex: /^[A-Za-z0-9 \/\\|:;&#()_$@',-]{0,100}$/,
    message:
      "Only alphanumeric characters and / \\ | : ; & # ( ) _ $ @ ' , - are allowed, up to 100 characters.",
  },
};

const TextFieldsWithoutSomeSpecialCharacters = {
  EngagementCode: {
    regex: /^[A-Za-z0-9-]+$/,
    message: "Special characters are not allowed.",
  },
  ActivityCode: {
    regex: /^[A-Za-z0-9]+$/,
    message: "Only alphanumeric characters are allowed.",
  },
  AuditTeamInfoRequiredId: {
    regex: /^[A-Za-z0-9]*$/,
    message: "Special characters are not allowed.",
  },
};

const emailFieldValidation = {
  AuditTeamMainContact: {
    regex: EY_OR_SRB_EMAIL_REGEX,
    message: "Only valid ey.com or @srb.in email addresses are allowed.",
  },
  AdditionalEngagementTeamMembers: {
    regex: EY_OR_SRB_EMAIL_REGEX,
    message: "Only valid ey.com or @srb.in email addresses are allowed.",
  },
  Manager: {
    regex: EY_OR_SRB_EMAIL_REGEX,
    message: "Only valid ey.com or @srb.in email addresses are allowed.",
  },
  SeniorManager: {
    regex: EY_OR_SRB_EMAIL_REGEX,
    message: "Only valid ey.com or @srb.in email addresses are allowed.",
  },
  EngagementPartner: {
    regex: EY_OR_SRB_EMAIL_REGEX,
    message: "Only valid ey.com or @srb.in email addresses are allowed.",
  },
  AdditionalDIDTeamMembers: {
    regex: EY_OR_SRB_EMAIL_REGEX,
    message: "Only valid ey.com or @srb.in email addresses are allowed.",
  },
  RegionalLead: {
    regex: EY_OR_SRB_EMAIL_REGEX,
    message: "Only valid ey.com or @srb.in email addresses are allowed.",
  },
  Preparer: {
    regex: EY_OR_SRB_EMAIL_REGEX,
    message: "Only valid ey.com or @srb.in email addresses are allowed.",
  },
  PreparerID: {
    regex: EY_OR_SRB_EMAIL_REGEX,
    message: "Only valid ey.com or @srb.in email addresses are allowed.",
  },
  Reviewer: {
    regex: EY_OR_SRB_EMAIL_REGEX,
    message: "Only valid ey.com or @srb.in email addresses are allowed.",
  },
  ReviewerID: {
    regex: EY_OR_SRB_EMAIL_REGEX,
    message: "Only valid ey.com or @srb.in email addresses are allowed.",
  },
  Approver: {
    regex: EY_OR_SRB_EMAIL_REGEX,
    message: "Only valid ey.com or @srb.in email addresses are allowed.",
  },
  ApproverID: {
    regex: EY_OR_SRB_EMAIL_REGEX,
    message: "Only valid ey.com or @srb.in email addresses are allowed.",
  },
};

const OtherSpecialFields = {
  HelixAnalyzerId: {
    regex: /^(?!\d+$).*$/,
    regexTarget: "displayValue",
    message: "Numeric values are not allowed.",
  },
  EngagementName: {
    regex: /^[A-Za-z0-9 .\/\\|:;&#()_$@',-]{0,100}$/,

    message:
      "Only English letters, numbers, spaces, and . / \\ | : ; & # ( ) _ $ @ ' , - are allowed, up to 100 characters.",

    // message:"Only alphanumeric characters and / \\ | : ; & # ( ) _ $ @ ' , - are allowed, up to 100 characters.",
    //regex: /^[A-Za-z0-9 .\/\\|:;&#()_$@',-]*$/,
    // regex: /^[\x20-\x7E]*$/,
    // message:
    //   "Only English letters, numbers, spaces, and special characters are allowed.",
  },
};

const extraValidations = {
  // Add any extra field validations that don't fit the above categories here
};

export const FIELD_VALIDATIONS = {
  // Add your field validations below:
  ...Efforts_and_Savings,
  ...OtherSpecialFields,
  ...NumericFields,
  ...emailFieldValidation,
  ...TextFieldsWithSomeSpecialCharacters,
  ...TextFieldsWithoutSomeSpecialCharacters,
  ...extraValidations,
};

// Contributor role fields that must be unique (no same person in multiple roles)
export const CONTRIBUTOR_UNIQUE_FIELDS = [
  "PreparerID",
  "ReviewerID",
  "ApproverID",
];

const CONTRIBUTOR_FIELD_ALIASES = {
  Preparer: "PreparerID",
  PreparerID: "PreparerID",
  Reviewer: "ReviewerID",
  ReviewerID: "ReviewerID",
  Approver: "ApproverID",
  ApproverID: "ApproverID",
};

// Validation message for duplicate contributors
export const CONTRIBUTOR_DUPLICATE_MESSAGE =
  "Preparer, Reviewer, and Approver must be different users";

// Helper function to get validation for a field
export const getFieldValidation = (fieldName) => {
  const canonicalContributorField = CONTRIBUTOR_FIELD_ALIASES[fieldName];

  return (
    FIELD_VALIDATIONS[fieldName] ||
    (canonicalContributorField
      ? FIELD_VALIDATIONS[canonicalContributorField]
      : null) ||
    null
  );
};

// Helper function to check if field has validation
export const hasFieldValidation = (fieldName) => {
  return (
    FIELD_VALIDATIONS.hasOwnProperty(fieldName) ||
    Boolean(CONTRIBUTOR_FIELD_ALIASES[fieldName])
  );
};

// Helper function to check if a field is a contributor field
export const isContributorField = (fieldName) => {
  return Boolean(CONTRIBUTOR_FIELD_ALIASES[fieldName]);
};

// Helper function to validate contributor uniqueness
export const validateContributorUniqueness = (
  fieldKey,
  value,
  allFieldValues,
) => {
  const canonicalFieldKey = CONTRIBUTOR_FIELD_ALIASES[fieldKey];

  if (!canonicalFieldKey || !value) {
    return { isValid: true, message: "" };
  }

  const contributorValues = allFieldValues
    .filter((fieldEntry) => {
      const canonicalFieldName =
        CONTRIBUTOR_FIELD_ALIASES[fieldEntry?.fieldName || ""];

      return canonicalFieldName && canonicalFieldName !== canonicalFieldKey;
    })
    .map((fieldEntry) => fieldEntry?.updatedValue || "")
    .filter((fieldValue) => fieldValue !== "");

  // Check if current value exists in other contributor fields
  if (contributorValues.includes(value)) {
    return { isValid: false, message: CONTRIBUTOR_DUPLICATE_MESSAGE };
  }

  return { isValid: true, message: "" };
};

export default FIELD_VALIDATIONS;
